import React, { Component } from 'react';
import { Modal, Select, Tag, Spin, Tabs, Divider, Radio } from 'antd';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import { Configuration } from '../Configuration';
import 'antd/dist/antd.css';
import 'sweetalert2/src/sweetalert2.scss';

const { Option } = Select;
const { TabPane } = Tabs;

const API_BASE = Configuration.SYSTEM_SETTINGS.API_URL;

async function pvFetch(method, path, body) {
  const opts = { method, headers: { 'Content-Type': 'application/json' } };
  if (body !== undefined) opts.body = JSON.stringify(body);
  const res = await fetch(API_BASE + path, opts);
  return res.json();
}

export default class frmPortalValidator extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // ── config (from DB) ─────────────────────────────────────────────────
      portalConfig: {},   // portalKey -> { portalId, name, mandatory:[{id,name}], optional:[{id,name}] }
      portalOrder: [],
      configLoading: true,

      // ── validation ───────────────────────────────────────────────────────
      selectedPortals: [],
      headerRow: 3,       // which Excel row contains column headers (1-indexed)
      rawWorkbook: null,  // stored so we can re-read when headerRow changes
      fileSheets: [],     // [{ name, data }] — all sheets in the uploaded file
      selectedSheets: [], // sheet names the user wants to include
      fileData: [],       // combined rows from selectedSheets
      fileColumns: [],
      fileName: '',
      mappings: {},       // { portalKey: { reqColName: fileColName | '__DEFAULT__' } }
      validationModalVisible: false,
      downloadModalVisible: false,
      activeTab: '',
      stats: {},          // { portalKey: { complete, incomplete } }
      loading: false,

      // ── management ───────────────────────────────────────────────────────
      manageModalVisible: false,
      editingPortalKey: null,
      newPortalName: '',
      addColumnText: '',
      addColumnType: 'mandatory',
    };
    this.fileInputRef = React.createRef();
    this._renameTimers = {};
  }

  componentDidMount() {
    this.loadConfig();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  //  CONFIG
  // ═══════════════════════════════════════════════════════════════════════════

  async loadConfig() {
    this.setState({ configLoading: true });
    try {
      const data = await pvFetch('GET', 'api/portal/config');
      if (data.status === 'ok') {
        const config = {};
        const order = [];
        (data.portals || []).forEach(p => {
          config[p.portalKey] = {
            portalId: p.portalId,
            name: p.portalName,
            mandatory: p.mandatory || [],
            optional: p.optional || [],
          };
          order.push(p.portalKey);
        });
        this.setState({ portalConfig: config, portalOrder: order, configLoading: false });
      } else {
        throw new Error(data.message || 'Failed');
      }
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Could not load portal config', text: String(e), timer: 3000, showConfirmButton: false });
      this.setState({ configLoading: false });
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  //  MANAGEMENT — PORTALS
  // ═══════════════════════════════════════════════════════════════════════════

  addNewPortal = async () => {
    const name = this.state.newPortalName.trim();
    if (!name) return;
    const key = name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    if (!key) {
      Swal.fire({ icon: 'warning', title: 'Invalid portal name', timer: 1500, showConfirmButton: false });
      return;
    }
    const res = await pvFetch('POST', 'api/portal/portal', { key, name });
    if (res.status === 'ok') {
      this.setState({ newPortalName: '' });
      await this.loadConfig();
      this.setState({ editingPortalKey: key });
    } else {
      Swal.fire({ icon: 'error', title: res.message || 'Failed to add portal', timer: 2000, showConfirmButton: false });
    }
  };

  deletePortal = async (key) => {
    const { portalConfig } = this.state;
    const result = await Swal.fire({
      icon: 'warning',
      title: `Delete "${portalConfig[key]?.name}"?`,
      text: 'All its columns will also be removed.',
      showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: '#ff4d4f',
    });
    if (!result.isConfirmed) return;
    const pid = portalConfig[key]?.portalId;
    await pvFetch('DELETE', `api/portal/portal/${pid}`);
    await this.loadConfig();
    this.setState(s => ({
      editingPortalKey: s.editingPortalKey === key ? null : s.editingPortalKey,
      selectedPortals: s.selectedPortals.filter(k => k !== key),
    }));
  };

  handleRenamePortal = (key, newName) => {
    this.setState(s => ({
      portalConfig: { ...s.portalConfig, [key]: { ...s.portalConfig[key], name: newName } },
    }));
    clearTimeout(this._renameTimers[key]);
    this._renameTimers[key] = setTimeout(() => {
      const { portalConfig } = this.state;
      const pid = portalConfig[key]?.portalId;
      const nm  = portalConfig[key]?.name?.trim();
      if (pid && nm) pvFetch('PUT', `api/portal/portal/${pid}`, { name: nm });
    }, 600);
  };

  // ═══════════════════════════════════════════════════════════════════════════
  //  MANAGEMENT — COLUMNS
  // ═══════════════════════════════════════════════════════════════════════════

  addColumn = async () => {
    const { editingPortalKey, addColumnText, addColumnType, portalConfig } = this.state;
    const name = addColumnText.trim();
    if (!name || !editingPortalKey) return;
    const portal = portalConfig[editingPortalKey];
    const exists = [...portal.mandatory, ...portal.optional].some(
      c => c.name.toLowerCase() === name.toLowerCase()
    );
    if (exists) {
      Swal.fire({ icon: 'warning', title: 'Column already exists', timer: 1300, showConfirmButton: false });
      return;
    }
    const res = await pvFetch('POST', 'api/portal/column', {
      portalId: portal.portalId, name, type: addColumnType,
    });
    if (res.status === 'ok') {
      const newCol = { id: res.columnId, name, sortOrder: portal[addColumnType].length };
      this.setState(s => ({
        addColumnText: '',
        portalConfig: {
          ...s.portalConfig,
          [editingPortalKey]: {
            ...s.portalConfig[editingPortalKey],
            [addColumnType]: [...s.portalConfig[editingPortalKey][addColumnType], newCol],
          },
        },
      }));
    }
  };

  deleteColumn = async (type, colId) => {
    await pvFetch('DELETE', `api/portal/column/${colId}`);
    const { editingPortalKey } = this.state;
    this.setState(s => ({
      portalConfig: {
        ...s.portalConfig,
        [editingPortalKey]: {
          ...s.portalConfig[editingPortalKey],
          [type]: s.portalConfig[editingPortalKey][type].filter(c => c.id !== colId),
        },
      },
    }));
  };

  moveColumn = async (type, idx, direction) => {
    const { editingPortalKey, portalConfig } = this.state;
    const list = [...portalConfig[editingPortalKey][type]];
    const target = idx + direction;
    if (target < 0 || target >= list.length) return;
    [list[idx], list[target]] = [list[target], list[idx]];
    const updated = list.map((c, i) => ({ ...c, sortOrder: i }));
    this.setState(s => ({
      portalConfig: {
        ...s.portalConfig,
        [editingPortalKey]: { ...s.portalConfig[editingPortalKey], [type]: updated },
      },
    }));
    await pvFetch('POST', 'api/portal/columns/reorder', updated.map(c => ({ id: c.id, sortOrder: c.sortOrder })));
  };

  // ═══════════════════════════════════════════════════════════════════════════
  //  VALIDATION
  // ═══════════════════════════════════════════════════════════════════════════

  handlePortalsChange = (vals) => {
    this.setState({
      selectedPortals: vals,
      fileSheets: [], selectedSheets: [],
      fileData: [], fileColumns: [], mappings: {}, fileName: '', stats: {},
    });
    if (this.fileInputRef.current) this.fileInputRef.current.value = '';
  };

  // Read a single worksheet using row 3 (index 2) as the header row
  _readSheetRows(ws, headerRowNum) {
    const hIdx = Math.max(0, (parseInt(headerRowNum) || 3) - 1); // convert to 0-based
    const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
    if (raw.length <= hIdx) return [];
    const headers = raw[hIdx];
    return raw.slice(hIdx + 1).map(row => {
      const obj = {};
      headers.forEach((h, i) => {
        if (h !== null && h !== undefined && String(h).trim() !== '') {
          obj[String(h).trim()] = row[i] !== undefined ? row[i] : '';
        }
      });
      return obj;
    });
  }

  // Extract/re-process workbook sheets using current headerRow
  _processWorkbook(wb, headerRowNum) {
    const MATCH_KEYWORDS = ['kids', 'men', 'women'];
    const matchedNames = wb.SheetNames.filter(n =>
      MATCH_KEYWORDS.some(kw => n.trim().toLowerCase().includes(kw))
    );
    const relevantNames = matchedNames.length ? matchedNames : wb.SheetNames;
    return relevantNames.map(name => ({
      name,
      data: this._readSheetRows(wb.Sheets[name], headerRowNum),
    }));
  }

  handleHeaderRowChange = (val) => {
    // Let the field go empty while typing. Coercing on every keystroke made it
    // snap back to 1 the moment you backspaced, so the number could never be
    // replaced. The value is coerced wherever it's actually used to read a
    // sheet, and handleHeaderRowBlur restores a valid row if left blank.
    if (val === '' || val === null || val === undefined) {
      this.setState({ headerRow: '' });
      return;
    }
    const newRow = Math.max(1, parseInt(val) || 1);
    const { rawWorkbook, selectedPortals, validationModalVisible } = this.state;
    this.setState({ headerRow: newRow });
    // Re-read immediately if modal is already open
    if (rawWorkbook && validationModalVisible) {
      const fileSheets = this._processWorkbook(rawWorkbook, newRow);
      const hasData = fileSheets.some(s => s.data.length > 0);
      if (!hasData) return;
      const selectedSheets = fileSheets.filter(s => s.data.length > 0).map(s => s.name);
      const { rows: fileData, columns: fileColumns } = this._mergeSheetData(selectedSheets, fileSheets);
      const mappings = {}, stats = {};
      selectedPortals.forEach(pk => {
        mappings[pk] = this.computeAutoMappings(fileColumns, pk);
        stats[pk] = this.computeStats(mappings[pk], fileData, pk);
      });
      this.setState({ fileSheets, selectedSheets, fileData, fileColumns, mappings, stats });
    }
  };

  // If the header-row field is left blank, restore a usable value on blur so
  // the app never tries to read a sheet with no header row.
  handleHeaderRowBlur = () => {
    if (this.state.headerRow === '') this.handleHeaderRowChange(1);
  };

  // Combine rows from the selected sheets and derive merged column list
  _mergeSheetData(selectedSheets, fileSheets) {
    const combined = [];
    const colSet = new Set();
    selectedSheets.forEach(name => {
      const sheet = fileSheets.find(s => s.name === name);
      if (!sheet) return;
      sheet.data.forEach(row => {
        const r = { ...row, _Sheet: name };
        Object.keys(r).forEach(k => colSet.add(k));
        combined.push(r);
      });
    });
    colSet.delete('_Sheet');
    return { rows: combined, columns: [...colSet] };
  }

  handleFileUpload = (event) => {
    const { selectedPortals } = this.state;
    // Field may be mid-edit (blank); fall back to row 1 rather than reading
    // with no header row or printing "in row ." in the error message.
    const headerRow = Math.max(1, parseInt(this.state.headerRow) || 1);
    if (!selectedPortals.length) {
      Swal.fire({ icon: 'warning', title: 'Select at least one portal first', timer: 1500, showConfirmButton: false });
      event.target.value = '';
      return;
    }
    const file = event.target.files[0];
    if (!file) return;
    this.setState({ loading: true });
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
        const fileSheets = this._processWorkbook(wb, headerRow);
        const hasData = fileSheets.some(s => s.data.length > 0);
        if (!hasData) {
          Swal.fire({ icon: 'error', title: 'No data found in matched sheets', text: `Make sure column headers are in row ${headerRow}.`, timer: 2500, showConfirmButton: false });
          this.setState({ loading: false });
          return;
        }
        const selectedSheets = fileSheets.filter(s => s.data.length > 0).map(s => s.name);
        const { rows: fileData, columns: fileColumns } = this._mergeSheetData(selectedSheets, fileSheets);
        const mappings = {};
        const stats = {};
        selectedPortals.forEach(pk => {
          mappings[pk] = this.computeAutoMappings(fileColumns, pk);
          stats[pk] = this.computeStats(mappings[pk], fileData, pk);
        });
        this.setState({
          rawWorkbook: wb,
          fileSheets, selectedSheets,
          fileData, fileColumns, fileName: file.name,
          mappings, stats,
          validationModalVisible: true,
          activeTab: selectedPortals[0],
          loading: false,
        });
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Error reading file', text: err.message, timer: 2500, showConfirmButton: false });
        this.setState({ loading: false });
      }
    };
    reader.readAsArrayBuffer(file);
  };

  handleSheetsChange = (names) => {
    const { fileSheets, selectedPortals } = this.state;
    if (!names.length) return;
    const { rows: fileData, columns: fileColumns } = this._mergeSheetData(names, fileSheets);
    const mappings = {};
    const stats = {};
    selectedPortals.forEach(pk => {
      mappings[pk] = this.computeAutoMappings(fileColumns, pk);
      stats[pk] = this.computeStats(mappings[pk], fileData, pk);
    });
    this.setState({ selectedSheets: names, fileData, fileColumns, mappings, stats });
  };

  computeAutoMappings(fileColumns, portalKey) {
    const config = this.state.portalConfig[portalKey];
    if (!config) return {};
    const norm = s => s.trim().toLowerCase();
    const fileNorm = fileColumns.map(c => ({ orig: c, norm: norm(c) }));
    const result = {};
    [...config.mandatory, ...config.optional].forEach(col => {
      const match = fileNorm.find(fc => fc.norm === norm(col.name));
      if (match) result[col.name] = match.orig;
    });
    return result;
  }

  computeStats(mappings, fileData, portalKey) {
    const config = this.state.portalConfig[portalKey];
    if (!config || !fileData) return { complete: 0, incomplete: 0 };
    let complete = 0, incomplete = 0;
    fileData.forEach(row => {
      const ok = config.mandatory.every(col => {
        const fc = mappings[col.name];
        if (fc === '__DEFAULT__') return true; // excluded from completeness check
        if (!fc) return false;
        const v = row[fc];
        return v !== null && v !== undefined && String(v).trim() !== '';
      });
      if (ok) complete++; else incomplete++;
    });
    return { complete, incomplete };
  }

  handleMappingChange = (portalKey, reqCol, fileCol) => {
    const { fileData } = this.state;
    this.setState(s => {
      const pm = { ...s.mappings[portalKey] };
      if (fileCol) pm[reqCol] = fileCol; else delete pm[reqCol];
      return {
        mappings: { ...s.mappings, [portalKey]: pm },
        stats:    { ...s.stats, [portalKey]: this.computeStats(pm, fileData, portalKey) },
      };
    });
  };

  // Opens the download-type chooser instead of downloading directly
  handleGenerate = () => {
    this.setState({ downloadModalVisible: true });
  };

  // Called when user picks Complete or Incomplete in the chooser popup
  handleDownload = (type) => {
    const { fileData, selectedPortals, mappings, portalConfig } = this.state;
    const wb = XLSX.utils.book_new();

    selectedPortals.forEach(pk => {
      const config = portalConfig[pk];
      if (!config) return;
      const pm = mappings[pk] || {};
      const allCols = [...config.mandatory, ...config.optional];
      const rows = [];

      fileData.forEach(row => {
        const out = {};
        allCols.forEach(col => {
          const fc = pm[col.name];
          if (fc === '__DEFAULT__') {
            out[col.name] = ''; // default = empty, excluded from completeness
          } else {
            out[col.name] = fc !== undefined ? (row[fc] !== undefined ? row[fc] : '') : '';
          }
        });
        const isComplete = config.mandatory.every(col => {
          const fc = pm[col.name];
          if (fc === '__DEFAULT__') return true; // excluded from completeness check
          const v = out[col.name];
          return v !== null && v !== undefined && String(v).trim() !== '';
        });
        if (type === 'complete' && isComplete) {
          rows.push(out);
        } else if (type === 'incomplete' && !isComplete) {
          // Remark lists the mandatory columns that are MISSING for this row
          const missingCols = config.mandatory
            .filter(col => {
              const fc = pm[col.name];
              if (fc === '__DEFAULT__') return false;
              const v = out[col.name];
              return fc === undefined || v === null || v === undefined || String(v).trim() === '';
            })
            .map(col => col.name);
          out['Remark'] = missingCols.length > 0
            ? 'Missing: ' + missingCols.join(', ')
            : 'Incomplete';
          rows.push(out);
        }
      });

      if (rows.length) {
        const suffix = type === 'complete' ? '_Complete' : '_Incomplete';
        const sheetName = (config.name.slice(0, 31 - suffix.length) + suffix);
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), sheetName);
      }
    });

    if (!wb.SheetNames.length) {
      Swal.fire({ icon: 'info', title: 'No data to export', timer: 1500, showConfirmButton: false });
      return;
    }

    const d = new Date();
    const stamp = String(d.getDate()).padStart(2,'0') + String(d.getMonth()+1).padStart(2,'0') + d.getFullYear();
    const label = type === 'complete' ? 'Complete' : 'Incomplete';
    XLSX.writeFile(wb, `Portal_${label}_${stamp}.xlsx`);
    Swal.fire({
      position: 'top-end', icon: 'success',
      title: `${label} data downloaded!`,
      showConfirmButton: false, timer: 2000,
    });
  };

  // ═══════════════════════════════════════════════════════════════════════════
  //  RENDER — MANAGEMENT MODAL
  // ═══════════════════════════════════════════════════════════════════════════

  renderColumnList(type) {
    const { editingPortalKey, portalConfig } = this.state;
    if (!editingPortalKey) return null;
    const portal = portalConfig[editingPortalKey];
    const list   = portal[type] || [];
    const isMandatory = type === 'mandatory';
    const color  = isMandatory ? '#ff4d4f' : '#faad14';
    const bg     = isMandatory ? '#fff2f0' : '#fffbe6';
    const border = isMandatory ? '#ffccc7' : '#ffe58f';

    return (
      <div>
        <div style={{ fontWeight: 700, fontSize: 11, color, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
          {isMandatory ? 'Mandatory' : 'Optional'} ({list.length})
        </div>
        {list.length === 0 && <div style={{ color: '#aaa', fontSize: 12, marginBottom: 8 }}>No columns yet.</div>}
        {list.map((col, idx) => (
          <div key={col.id} style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4 }}>
            <div style={{ flex: 1, padding: '3px 9px', fontSize: 12, borderRadius: 4, background: bg, border: `1px solid ${border}` }}>
              {col.name}
            </div>
            <button className="btn btn-xs btn-default" onClick={() => this.moveColumn(type, idx, -1)} disabled={idx === 0} style={{ padding: '1px 5px' }}>▲</button>
            <button className="btn btn-xs btn-default" onClick={() => this.moveColumn(type, idx, 1)} disabled={idx === list.length - 1} style={{ padding: '1px 5px' }}>▼</button>
            <button className="btn btn-xs btn-danger" onClick={() => this.deleteColumn(type, col.id)} style={{ padding: '1px 5px' }}>✕</button>
          </div>
        ))}
      </div>
    );
  }

  renderManageModal() {
    const { manageModalVisible, portalConfig, portalOrder, editingPortalKey, newPortalName, addColumnText, addColumnType } = this.state;
    const editingPortal = editingPortalKey ? portalConfig[editingPortalKey] : null;

    return (
      <Modal
        title="Manage Portals & Columns"
        visible={manageModalVisible}
        onCancel={() => this.setState({ manageModalVisible: false })}
        width="82%" style={{ top: 55 }}
        bodyStyle={{ padding: '16px 20px' }}
        footer={<button className="btn btn-default" onClick={() => this.setState({ manageModalVisible: false })}>Close</button>}
      >
        <div style={{ display: 'flex', gap: 20, minHeight: 420 }}>

          {/* Portal list */}
          <div style={{ flex: '0 0 200px', borderRight: '1px solid #f0f0f0', paddingRight: 16, display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: '#1890ff', letterSpacing: 1, marginBottom: 10 }}>
              Portals ({portalOrder.length})
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {portalOrder.map(key => {
                const p = portalConfig[key];
                if (!p) return null;
                return (
                  <div key={key}
                    style={{
                      padding: '7px 10px', marginBottom: 5, borderRadius: 4, cursor: 'pointer', fontSize: 13,
                      background: editingPortalKey === key ? '#e6f7ff' : '#fafafa',
                      border: `1px solid ${editingPortalKey === key ? '#91d5ff' : '#e8e8e8'}`,
                      fontWeight: editingPortalKey === key ? 700 : 400,
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}
                    onClick={() => this.setState({ editingPortalKey: key, addColumnText: '' })}
                  >
                    <span style={{ wordBreak: 'break-all', flex: 1 }}>{p.name}</span>
                    <button className="btn btn-xs btn-danger" style={{ marginLeft: 6, padding: '1px 5px', flexShrink: 0 }}
                      onClick={ev => { ev.stopPropagation(); this.deletePortal(key); }}>✕</button>
                  </div>
                );
              })}
            </div>
            <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 12, marginTop: 12 }}>
              <input className="form-control" placeholder="New portal name"
                value={newPortalName}
                onChange={e => this.setState({ newPortalName: e.target.value })}
                onKeyDown={e => e.key === 'Enter' && this.addNewPortal()}
                style={{ marginBottom: 6, fontSize: 12 }} />
              <button className="btn btn-primary btn-block btn-sm" onClick={this.addNewPortal}>+ Add Portal</button>
            </div>
          </div>

          {/* Column editor */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {editingPortal ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <span style={{ fontWeight: 700, fontSize: 14, flexShrink: 0 }}>Editing:</span>
                  <input className="form-control" style={{ maxWidth: 230, fontWeight: 600 }}
                    value={editingPortal.name}
                    onChange={e => this.handleRenamePortal(editingPortalKey, e.target.value)} />
                  <Tag color="red">{editingPortal.mandatory.length} mandatory</Tag>
                  <Tag color="blue">{editingPortal.optional.length} optional</Tag>
                </div>
                <div style={{ display: 'flex', gap: 20 }}>
                  <div style={{ flex: 1 }}>{this.renderColumnList('mandatory')}</div>
                  <div style={{ flex: 1 }}>{this.renderColumnList('optional')}</div>
                </div>
                <Divider style={{ margin: '12px 0' }} />
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Radio.Group value={addColumnType} onChange={e => this.setState({ addColumnType: e.target.value })} buttonStyle="solid" size="small">
                    <Radio.Button value="mandatory">
                      <span style={{ color: addColumnType === 'mandatory' ? '#fff' : '#ff4d4f' }}>Mandatory</span>
                    </Radio.Button>
                    <Radio.Button value="optional">
                      <span style={{ color: addColumnType === 'optional' ? '#fff' : '#faad14' }}>Optional</span>
                    </Radio.Button>
                  </Radio.Group>
                  <input className="form-control" placeholder="Column name — Enter or click Add"
                    value={addColumnText}
                    onChange={e => this.setState({ addColumnText: e.target.value })}
                    onKeyDown={e => e.key === 'Enter' && this.addColumn()}
                    style={{ flex: 1, fontSize: 13 }} />
                  <button className="btn btn-success" onClick={this.addColumn} style={{ whiteSpace: 'nowrap' }}>
                    + Add Column
                  </button>
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center', color: '#aaa', marginTop: 80, fontSize: 14 }}>
                ← Select a portal to edit its columns
              </div>
            )}
          </div>
        </div>
      </Modal>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  //  RENDER — MAPPING TAB (one portal)
  // ═══════════════════════════════════════════════════════════════════════════

  renderMappingSection(portalKey, cols, type) {
    const { fileColumns, mappings } = this.state;
    const pm = mappings[portalKey] || {};
    const isMandatory = type === 'mandatory';
    const accentColor = isMandatory ? '#ff4d4f' : '#faad14';
    const bgMapped = '#f6ffed', borderMapped = '#b7eb8f';
    const bgUnmapped = isMandatory ? '#fff2f0' : '#fffbe6';
    const borderUnmapped = isMandatory ? '#ffccc7' : '#ffe58f';

    return (
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontWeight: 700, fontSize: 11, color: accentColor, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>
          {isMandatory ? 'Mandatory' : 'Optional'} ({cols.length})
        </div>
        {cols.map(col => {
          const mapped = pm[col.name];
          const isDefault = mapped === '__DEFAULT__';
          const isSatisfied = mapped && !isDefault;
          const bgCol = isDefault ? '#fffbe6' : (isSatisfied ? bgMapped : bgUnmapped);
          const borderCol = isDefault ? '#ffe58f' : (isSatisfied ? borderMapped : borderUnmapped);
          return (
            <div key={col.id} style={{ display: 'flex', alignItems: 'center', marginBottom: 6, gap: 8 }}>
              <div style={{
                flex: '0 0 190px', fontSize: 11, padding: '4px 7px', borderRadius: 4,
                lineHeight: '1.35', wordBreak: 'break-word',
                background: bgCol, border: `1px solid ${borderCol}`,
              }}>
                {col.name}
              </div>
              <span style={{ color: '#bbb', fontSize: 12, flexShrink: 0 }}>→</span>
              <Select allowClear size="small" style={{ flex: 1, minWidth: 140 }}
                placeholder="— select file column —"
                value={pm[col.name] || undefined}
                onChange={val => this.handleMappingChange(portalKey, col.name, val)}
                showSearch
                filterOption={(input, opt) => {
                  const txt = typeof opt.children === 'string' ? opt.children : '';
                  return txt.toLowerCase().includes(input.toLowerCase());
                }}
              >
                <Option value="__DEFAULT__" style={{ color: '#888', fontStyle: 'italic', borderBottom: '1px solid #f0f0f0' }}>
                  ⊘ Input value - Default (exclude from check)
                </Option>
                {fileColumns.map(fc => <Option key={fc} value={fc}>{fc}</Option>)}
              </Select>
              <span style={{ fontSize: 13, flexShrink: 0, color: isDefault ? '#faad14' : (isSatisfied ? '#52c41a' : (isMandatory ? '#ff4d4f' : '#faad14')) }}>
                {isDefault ? '⊘' : (isSatisfied ? '✓' : '○')}
              </span>
            </div>
          );
        })}
      </div>
    );
  }

  renderMappingTab(portalKey) {
    const { portalConfig, fileColumns, mappings, stats } = this.state;
    const config = portalConfig[portalKey];
    if (!config) return null;
    const pm     = mappings[portalKey] || {};
    const pStats = stats[portalKey] || { complete: 0, incomplete: 0 };
    // Exclude the special sentinel from the file-column "mapped" highlights
    const mappedSet = new Set(Object.values(pm).filter(v => v !== '__DEFAULT__'));
    const unmapped = config.mandatory.filter(c => !pm[c.name]).length;

    return (
      <div style={{ display: 'flex', gap: 20, maxHeight: 'calc(65vh - 90px)' }}>
        {/* File columns panel */}
        <div style={{ flex: '0 0 200px', borderRight: '1px solid #f0f0f0', paddingRight: 14, overflowY: 'auto' }}>
          <div style={{ fontWeight: 700, fontSize: 11, marginBottom: 8, color: '#1890ff', textTransform: 'uppercase', letterSpacing: 1 }}>
            File Columns ({fileColumns.length})
          </div>
          {fileColumns.map(fc => {
            const isMapped = mappedSet.has(fc);
            return (
              <div key={fc} style={{
                padding: '4px 8px', marginBottom: 4, borderRadius: 4, fontSize: 11, wordBreak: 'break-all',
                background: isMapped ? '#f6ffed' : '#fafafa',
                border: `1px solid ${isMapped ? '#b7eb8f' : '#e8e8e8'}`,
                color: isMapped ? '#389e0d' : '#595959',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span>{fc}</span>
                {isMapped && <span style={{ marginLeft: 4, fontWeight: 700, flexShrink: 0 }}>✓</span>}
              </div>
            );
          })}
        </div>

        {/* Mapping panel */}
        <div style={{ flex: 1, overflowY: 'auto', paddingRight: 4 }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
            <Tag color="green">✓ Complete: {pStats.complete}</Tag>
            <Tag color="red">✗ Incomplete: {pStats.incomplete}</Tag>
            {unmapped > 0 && <Tag color="orange">{unmapped} mandatory not mapped</Tag>}
          </div>
          {this.renderMappingSection(portalKey, config.mandatory, 'mandatory')}
          <Divider style={{ margin: '10px 0' }} />
          {this.renderMappingSection(portalKey, config.optional, 'optional')}
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  //  RENDER — DOWNLOAD CHOOSER MODAL
  // ═══════════════════════════════════════════════════════════════════════════

  renderDownloadModal() {
    const { downloadModalVisible, stats } = this.state;
    const totalComplete   = Object.values(stats).reduce((s, st) => s + (st.complete   || 0), 0);
    const totalIncomplete = Object.values(stats).reduce((s, st) => s + (st.incomplete || 0), 0);

    return (
      <Modal
        title={<span style={{ fontWeight: 700 }}>Choose Download Type</span>}
        visible={downloadModalVisible}
        onCancel={() => this.setState({ downloadModalVisible: false })}
        width={480}
        style={{ top: 220 }}
        footer={null}
        destroyOnClose
      >
        <div style={{ display: 'flex', gap: 16, padding: '8px 0 4px' }}>
          {/* Complete */}
          <button
            onClick={() => this.handleDownload('complete')}
            style={{
              flex: 1, cursor: 'pointer', border: '2px solid #52c41a', borderRadius: 10,
              background: '#f6ffed', padding: '22px 12px', textAlign: 'center',
              transition: 'all .15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#d9f7be'}
            onMouseLeave={e => e.currentTarget.style.background = '#f6ffed'}
          >
            <div style={{ fontSize: 36, fontWeight: 800, color: '#52c41a', lineHeight: 1 }}>{totalComplete}</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#389e0d', marginTop: 6 }}>Complete Data</div>
            <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>All mandatory fields filled</div>
          </button>

          {/* Incomplete */}
          <button
            onClick={() => this.handleDownload('incomplete')}
            style={{
              flex: 1, cursor: 'pointer', border: '2px solid #ff4d4f', borderRadius: 10,
              background: '#fff2f0', padding: '22px 12px', textAlign: 'center',
              transition: 'all .15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#ffccc7'}
            onMouseLeave={e => e.currentTarget.style.background = '#fff2f0'}
          >
            <div style={{ fontSize: 36, fontWeight: 800, color: '#ff4d4f', lineHeight: 1 }}>{totalIncomplete}</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#cf1322', marginTop: 6 }}>Incomplete Data</div>
            <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>One or more mandatory fields missing</div>
          </button>
        </div>
      </Modal>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  //  RENDER — VALIDATION MODAL
  // ═══════════════════════════════════════════════════════════════════════════

  renderValidationModal() {
    const { validationModalVisible, selectedPortals, portalConfig, stats, fileData, fileName, activeTab, fileSheets, selectedSheets, headerRow } = this.state;
    if (!selectedPortals.length) return null;

    const totalComplete   = Object.values(stats).reduce((s, st) => s + (st.complete || 0), 0);
    const totalIncomplete = Object.values(stats).reduce((s, st) => s + (st.incomplete || 0), 0);

    return (
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: 15 }}>Column Mapping</span>
            <Tag color="blue" style={{ margin: 0 }}>{fileData.length} rows</Tag>
            <Tag color="default" style={{ margin: 0, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fileName}</Tag>
          </div>
        }
        visible={validationModalVisible}
        onCancel={() => this.setState({ validationModalVisible: false })}
        width="90%" style={{ top: 80 }}
        bodyStyle={{ padding: '12px 20px' }}
        destroyOnClose
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <Tag color="green" style={{ fontSize: 12, padding: '2px 10px' }}>Total Complete: {totalComplete}</Tag>
              <Tag color="red" style={{ fontSize: 12, padding: '2px 10px' }}>Total Incomplete: {totalIncomplete}</Tag>
              <span style={{ fontSize: 11, color: '#888' }}>
                → 1 Excel file with {selectedPortals.length * 2} sheets (Complete + Incomplete per portal)
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-default" onClick={() => this.setState({ validationModalVisible: false })}>Cancel</button>
              <button className="btn btn-success" onClick={this.handleGenerate}>Generate Excel File</button>
            </div>
          </div>
        }
      >
        {/* Header row changer + sheet selector */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#f6f9ff', border: '1px solid #d6e4ff', borderRadius: 6, padding: '8px 14px', marginBottom: 12, flexWrap: 'wrap' }}>
          {/* Header row input always visible in modal */}
          <span style={{ fontWeight: 600, fontSize: 12, color: '#1890ff', whiteSpace: 'nowrap' }}>Header row:</span>
          <input
            type="number" min="1" max="100"
            value={headerRow}
            onChange={e => this.handleHeaderRowChange(e.target.value)}
            onBlur={this.handleHeaderRowBlur}
            style={{ width: 52, padding: '1px 6px', fontSize: 12, border: '1px solid #91d5ff', borderRadius: 4, textAlign: 'center' }}
            title="Change the row that contains column headers and re-map automatically"
          />
          {fileSheets.length > 1 && (
            <>
              <span style={{ fontWeight: 600, fontSize: 12, color: '#1890ff', whiteSpace: 'nowrap', marginLeft: 6 }}>Sheets:</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {fileSheets.map(s => {
                  const active = selectedSheets.includes(s.name);
                  return (
                    <span
                      key={s.name}
                      onClick={() => {
                        const next = active
                          ? selectedSheets.filter(n => n !== s.name)
                          : [...selectedSheets, s.name];
                        if (next.length) this.handleSheetsChange(next);
                      }}
                      style={{
                        cursor: 'pointer', fontSize: 12, padding: '2px 10px', borderRadius: 12,
                        background: active ? '#1890ff' : '#f0f0f0',
                        color: active ? '#fff' : '#555',
                        border: `1px solid ${active ? '#1890ff' : '#d9d9d9'}`,
                        userSelect: 'none',
                      }}
                    >
                      {s.name}&ensp;<span style={{ opacity: 0.7 }}>({s.data.length} rows)</span>
                    </span>
                  );
                })}
              </div>
            </>
          )}
          <span style={{ fontSize: 11, color: '#888', whiteSpace: 'nowrap', marginLeft: 'auto' }}>
            (column names from row {headerRow || 1})
          </span>
        </div>

        <Tabs activeKey={activeTab} onChange={key => this.setState({ activeTab: key })} type="card" size="small">
          {selectedPortals.map(pk => {
            const config  = portalConfig[pk];
            if (!config) return null;
            const pm      = this.state.mappings[pk] || {};
            const unmapped = config.mandatory.filter(c => !pm[c.name]).length;
            return (
              <TabPane
                tab={
                  <span>
                    {config.name}&nbsp;
                    {unmapped > 0
                      ? <Tag color="orange" style={{ fontSize: 10, padding: '0 4px' }}>{unmapped}!</Tag>
                      : <Tag color="green"  style={{ fontSize: 10, padding: '0 4px' }}>✓</Tag>
                    }
                  </span>
                }
                key={pk}
              >
                {this.renderMappingTab(pk)}
              </TabPane>
            );
          })}
        </Tabs>
      </Modal>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ═══════════════════════════════════════════════════════════════════════════
  //  HELP MODAL
  // ═══════════════════════════════════════════════════════════════════════════

  help() {
    const stepStyle = { display: 'flex', gap: 9, marginBottom: 8, alignItems: 'flex-start' };
    const numStyle = {
      minWidth: 22, height: 22, borderRadius: '50%',
      background: '#3D9970', color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, fontSize: 11, flexShrink: 0,
    };
    const textStyle = { fontSize: 12, color: '#333', lineHeight: 1.5 };
    const sectionStyle = {
      background: '#f5f5f5', borderRadius: 5, padding: '10px 14px',
      marginBottom: 10, borderLeft: '3px solid #3D9970',
    };
    const sectionTitleStyle = {
      fontWeight: 700, fontSize: 12, textTransform: 'uppercase',
      letterSpacing: 0.5, color: '#3D9970', marginBottom: 8,
    };
    const noteStyle = {
      background: '#fffbe6', border: '1px solid #ffe58f',
      borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#555',
    };

    Modal.info({
      title: 'HOW TO USE — PORTAL DATA VALIDATOR',
      okText: 'CLOSE',
      width: Math.min(660, window.innerWidth * 0.92),
      closable: true,
      okButtonProps: { style: { display: 'none' } },
      footer: null,
      bodyStyle: { padding: '14px 18px' },
      style: { top: 70, padding: '0px' },
      maskClosable: true,
      content: (
        <div>

          {/* Step 1 — Setup Portals */}
          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>Step 1 — Set Up Portals (Admin / First Time)</div>
            <div style={stepStyle}>
              <div style={numStyle}>1</div>
              <div style={textStyle}><strong>Click "⚙ Manage Portals"</strong> (top-right) to open the portal configuration panel.</div>
            </div>
            <div style={stepStyle}>
              <div style={numStyle}>2</div>
              <div style={textStyle}><strong>Add a Portal</strong> — Enter a portal name and click <em>Add Portal</em>. Each portal represents a data destination (e.g., a marketplace or channel).</div>
            </div>
            <div style={stepStyle}>
              <div style={numStyle}>3</div>
              <div style={textStyle}><strong>Define Columns</strong> — For each portal, add <strong>Mandatory</strong> columns (required fields) and <strong>Optional</strong> columns. Portal configuration is saved to the database and shared with all users.</div>
            </div>
          </div>

          {/* Step 2 — Upload Excel */}
          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>Step 2 — Upload Your Excel File</div>
            <div style={stepStyle}>
              <div style={numStyle}>1</div>
              <div style={textStyle}><strong>Select Portal(s)</strong> — Pick one or more portals you want to validate data against. Use <em>All</em> to select every portal at once.</div>
            </div>
            <div style={stepStyle}>
              <div style={numStyle}>2</div>
              <div style={textStyle}><strong>Set Header Row</strong> — Enter the row number in your Excel file that contains the column headers (default: 3). Rows above it are treated as title/metadata rows and ignored.</div>
            </div>
            <div style={stepStyle}>
              <div style={numStyle}>3</div>
              <div style={textStyle}><strong>Choose File</strong> — Click the file picker and select your Excel file (.xls / .xlsx). All sheets in the workbook will be loaded automatically.</div>
            </div>
            <div style={stepStyle}>
              <div style={numStyle}>4</div>
              <div style={textStyle}><strong>Select Sheets</strong> — If your workbook has multiple sheets, choose which ones to include in the validation. By default all sheets are selected.</div>
            </div>
          </div>

          {/* Step 3 — Map Columns */}
          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>Step 3 — Map Columns</div>
            <div style={stepStyle}>
              <div style={numStyle}>1</div>
              <div style={textStyle}><strong>Column Mapping Tabs</strong> — A tab appears for each selected portal. Open each tab to map the portal's required columns to columns from your uploaded Excel file.</div>
            </div>
            <div style={stepStyle}>
              <div style={numStyle}>2</div>
              <div style={textStyle}><strong>Map or Set Default</strong> — For each portal column, either choose the matching Excel column from the dropdown, or select <em>"Use Default / Leave Blank"</em> to fill with an empty value.</div>
            </div>
            <div style={stepStyle}>
              <div style={numStyle}>3</div>
              <div style={textStyle}><strong>Validate</strong> — Click <em>Validate &amp; Preview</em> to check completeness. The system counts how many rows have all mandatory columns filled (<strong>Complete</strong>) vs. rows with missing values (<strong>Incomplete</strong>).</div>
            </div>
          </div>

          {/* Step 4 — Download */}
          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>Step 4 — Download Results</div>
            <div style={stepStyle}>
              <div style={numStyle}>1</div>
              <div style={textStyle}><strong>Download Complete</strong> — Exports rows where all mandatory columns for a portal are filled. Each portal gets its own sheet in the output file.</div>
            </div>
            <div style={stepStyle}>
              <div style={numStyle}>2</div>
              <div style={textStyle}><strong>Download Incomplete</strong> — Exports rows that are missing one or more mandatory values. Includes a <strong>Remark</strong> column listing which mandatory columns are already filled for each row, so you know what still needs to be completed.</div>
            </div>
          </div>

          {/* Notes */}
          <div style={noteStyle}>
            <strong>⚠ Important Notes</strong>
            <ul style={{ marginTop: 6, marginBottom: 0, paddingLeft: 18 }}>
              <li>Portal configuration (columns) is stored in the database — changes affect all users.</li>
              <li>Only <strong>.xls</strong> and <strong>.xlsx</strong> file formats are accepted.</li>
              <li>The <strong>Header Row</strong> number must match exactly where column names appear in your file.</li>
              <li>A row is <strong>Complete</strong> only when every mandatory column has a non-empty value.</li>
              <li>The <strong>Remark</strong> column in the Incomplete export lists which mandatory columns are already satisfied per row.</li>
            </ul>
          </div>

        </div>
      ),
    });
  }

  //  MAIN RENDER
  // ═══════════════════════════════════════════════════════════════════════════

  render() {
    const { loading, configLoading, selectedPortals, portalConfig, portalOrder } = this.state;
    const portals = portalOrder.map(k => ({ key: k, name: portalConfig[k]?.name || k }));

    return (
      <div className="content-wrapper">
        <div className="loader" style={{ display: (loading || configLoading) ? 'block' : 'none' }}>
          <div className="loader-item"><Spin /></div>
        </div>

        <section className="content">
          <div id="ctl00_CPHMaincontent_divclient" className="box box-success" style={{ marginTop: '-13px' }}>
            <div className="box-header with-border" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '12px 20px' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, color: '#aaa', marginBottom: 3 }}>
                  Dashboard &gt; Data Validator &gt; Portal Data Validator
                </div>
                <h3 className="box-title" style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
                  Portal Data Validator
                </h3>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
                <button className="btn btn-sm btn-default" style={{ marginTop: 4 }}
                  onClick={() => this.setState({ manageModalVisible: true, editingPortalKey: null })}>
                  ⚙ Manage Portals
                </button>
                <button className="btn btn-sm btn-default" style={{ marginTop: 4 }}
                  onClick={this.help.bind(this)}>
                  Help
                </button>
              </div>
            </div>

            <div className="box-body" style={{ display: 'block' }}>
              <div className="row">
                {/* Multi-select portal */}
                <div className="col-xs-12 col-sm-12 col-md-5 margintop">
                  <label>Select Portal(s) <span className="vcode">*</span></label>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <Select
                      mode="multiple" style={{ flex: 1 }}
                      placeholder="Select one or more portals"
                      value={selectedPortals}
                      onChange={this.handlePortalsChange}
                      maxTagCount={4}
                    >
                      {portals.map(p => <Option key={p.key} value={p.key}>{p.name}</Option>)}
                    </Select>
                    <button className="btn btn-xs btn-default" style={{ whiteSpace: 'nowrap' }}
                      onClick={() => this.handlePortalsChange(portals.map(p => p.key))}>All</button>
                    <button className="btn btn-xs btn-default" style={{ whiteSpace: 'nowrap' }}
                      onClick={() => { this.handlePortalsChange([]); this.handleHeaderRowChange(1); }}>Clear</button>
                  </div>
                </div>

                {/* Header row number */}
                <div className="col-xs-6 col-sm-3 col-md-1 margintop">
                  <label>Header Row <span className="vcode">*</span></label>
                  <input
                    type="number" min="1" max="100" className="form-control"
                    value={this.state.headerRow}
                    onChange={e => this.handleHeaderRowChange(e.target.value)}
                    onBlur={this.handleHeaderRowBlur}
                    style={{ padding: '4px 8px', textAlign: 'center' }}
                    title="Row number in the Excel file that contains column names"
                  />
                </div>

                {/* File upload */}
                <div className="col-xs-12 col-sm-6 col-md-3 margintop">
                  <label>Upload Excel File <span className="vcode">*</span></label>
                  <input type="file" accept=".xlsx,.xls" className="form-control"
                    ref={this.fileInputRef}
                    onChange={this.handleFileUpload}
                    disabled={!selectedPortals.length}
                    style={{ cursor: selectedPortals.length ? 'pointer' : 'not-allowed', padding: '4px 8px' }} />
                </div>

                {/* Portal badges */}
                {selectedPortals.length > 0 && (
                  <div className="col-xs-12 col-sm-12 col-md-4 margintop">
                    <label>&nbsp;</label>
                    <div style={{ padding: '8px 12px', background: '#f0f8ff', borderRadius: 4, border: '1px solid #91d5ff', fontSize: 12 }}>
                      {selectedPortals.map(pk => {
                        const c = portalConfig[pk];
                        if (!c) return null;
                        return (
                          <span key={pk} style={{ marginRight: 10, display: 'inline-block', marginBottom: 2 }}>
                            <strong>{c.name}</strong>&nbsp;
                            <Tag color="red"   style={{ fontSize: 11 }}>{c.mandatory.length}M</Tag>
                            <Tag color="blue"  style={{ fontSize: 11 }}>{c.optional.length}O</Tag>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {!selectedPortals.length && (
                <div className="row">
                  <div className="col-xs-12 margintop">
                    <div style={{ padding: '10px 14px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 4, fontSize: 13, color: '#614700' }}>
                      <strong>How to use:</strong>&ensp;
                      Use <em>⚙ Manage Portals</em> to add portals and define their columns (saved to database, shared with all users)
                      &nbsp;→&nbsp; Select one or more portals &nbsp;→&nbsp;
                      Upload your Excel file &nbsp;→&nbsp;
                      Map columns per portal tab &nbsp;→&nbsp;
                      Download one Excel with Complete &amp; Incomplete sheets per portal.
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {this.renderManageModal()}
        {this.renderValidationModal()}
        {this.renderDownloadModal()}
      </div>
    );
  }
}
